import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useAuth } from "@clerk/react";
import {
  Check,
  ImagePlus,
  Loader2,
  RefreshCw,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { useGalleryData } from "./api";
import { formatBytes, getImageDimensions, type Photo } from "./model";
import { AccessDeniedView, ErrorView, LoadingView, RequireSignInView, ThemedShell, ToolTopbar } from "./ui";

type UploadStatus = "queued" | "uploading" | "complete" | "error" | "cancelled";

type UploadItem = {
  id: string;
  fingerprint: string;
  file: File;
  previewUrl: string;
  progress: number;
  status: UploadStatus;
  error: string;
};

export function UploadPage({ slug }: { slug: string }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { gallery, error, loading, reload } = useGalleryData(slug);
  const [queue, setQueue] = useState<UploadItem[]>([]);
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const requests = useRef(new Map<string, XMLHttpRequest>());
  const previews = useRef(new Set<string>());

  useEffect(() => () => {
    requests.current.forEach((request) => request.abort());
    previews.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  function addFiles(fileList: FileList | File[]) {
    const imageFiles = Array.from(fileList).filter((file) => file.type.startsWith("image/"));
    let ignored = 0;
    setQueue((current) => {
      const known = new Set(current.map((item) => item.fingerprint));
      const additions: UploadItem[] = [];
      for (const file of imageFiles) {
        const fingerprint = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(fingerprint)) {
          ignored += 1;
          continue;
        }
        known.add(fingerprint);
        const previewUrl = URL.createObjectURL(file);
        previews.current.add(previewUrl);
        additions.push({
          id: crypto.randomUUID(),
          fingerprint,
          file,
          previewUrl,
          progress: 0,
          status: "queued",
          error: ""
        });
      }
      return [...current, ...additions];
    });
    setMessage(ignored ? `${ignored} duplicate ${ignored === 1 ? "file was" : "files were"} already in the queue.` : "");
  }

  function updateItem(id: string, update: Partial<UploadItem>) {
    setQueue((current) => current.map((item) => item.id === id ? { ...item, ...update } : item));
  }

  function removeItem(item: UploadItem) {
    requests.current.get(item.id)?.abort();
    URL.revokeObjectURL(item.previewUrl);
    previews.current.delete(item.previewUrl);
    setQueue((current) => current.filter((candidate) => candidate.id !== item.id));
  }

  function cancelItem(item: UploadItem) {
    requests.current.get(item.id)?.abort();
    updateItem(item.id, { status: "cancelled", error: "Upload cancelled" });
  }

  async function uploadItem(item: UploadItem) {
    updateItem(item.id, { status: "uploading", progress: 0, error: "" });
    try {
      const [token, dimensions] = await Promise.all([getToken(), getImageDimensions(item.file)]);
      if (!token) throw new Error("Your session expired. Sign in and retry.");

      const form = new FormData();
      form.append("file", item.file);
      if (dimensions.width) form.append("width", String(dimensions.width));
      if (dimensions.height) form.append("height", String(dimensions.height));

      await new Promise<Photo>((resolve, reject) => {
        const request = new XMLHttpRequest();
        requests.current.set(item.id, request);
        request.open("POST", `/api/galleries/${slug}/photos`);
        request.setRequestHeader("authorization", `Bearer ${token}`);
        request.upload.onprogress = (event) => {
          if (event.lengthComputable) updateItem(item.id, { progress: Math.round((event.loaded / event.total) * 100) });
        };
        request.onload = () => {
          requests.current.delete(item.id);
          const data = parseResponse(request.responseText);
          if (request.status >= 200 && request.status < 300 && data.photo) {
            resolve(data.photo as Photo);
          } else {
            reject(new Error(typeof data.error === "string" ? data.error : "Upload failed"));
          }
        };
        request.onerror = () => reject(new Error("Network interrupted the upload"));
        request.onabort = () => reject(new DOMException("Upload cancelled", "AbortError"));
        request.send(form);
      });
      updateItem(item.id, { status: "complete", progress: 100 });
    } catch (err) {
      requests.current.delete(item.id);
      const cancelled = err instanceof DOMException && err.name === "AbortError";
      updateItem(item.id, {
        status: cancelled ? "cancelled" : "error",
        error: cancelled ? "Upload cancelled" : err instanceof Error ? err.message : "Upload failed"
      });
    }
  }

  async function runUploads(items: UploadItem[]) {
    if (!items.length || isUploading) return;
    setIsUploading(true);
    setMessage(`Uploading ${items.length} ${items.length === 1 ? "original" : "originals"}...`);
    let cursor = 0;
    const worker = async () => {
      while (cursor < items.length) {
        const item = items[cursor];
        cursor += 1;
        await uploadItem(item);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, worker));
    setIsUploading(false);
    setMessage("Queue finished. Completed originals are already in the gallery.");
    reload();
  }

  function retryItem(item: UploadItem) {
    const retry = { ...item, status: "queued" as const, progress: 0, error: "" };
    updateItem(item.id, retry);
    void runUploads([retry]);
  }

  function clearCompleted() {
    setQueue((current) => {
      current.filter((item) => item.status === "complete").forEach((item) => {
        URL.revokeObjectURL(item.previewUrl);
        previews.current.delete(item.previewUrl);
      });
      return current.filter((item) => item.status !== "complete");
    });
  }

  if (!isLoaded || loading) return <LoadingView />;
  if (error || !gallery) return <ErrorView message={error || "Gallery not found"} />;
  if (!isSignedIn) return <RequireSignInView title="Sign in to upload" />;
  if (!gallery.isOwner) return <AccessDeniedView slug={slug} />;

  const queued = queue.filter((item) => item.status === "queued" || item.status === "cancelled");
  const completed = queue.filter((item) => item.status === "complete").length;
  const totalBytes = queue.reduce((total, item) => total + item.file.size, 0);

  function handleInput(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) addFiles(event.target.files);
    event.target.value = "";
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  }

  return (
    <ThemedShell theme={gallery.theme}>
      <ToolTopbar slug={slug} />
      <section className="tool-page upload-page">
        <div className="tool-heading">
          <h1>{gallery.displayName}</h1>
          <p className="intro compact">Add a large selection at once. Every file keeps its original bytes and uploads independently.</p>
        </div>

        <label
          className={`dropzone ${isDragging ? "is-dragging" : ""}`}
          aria-label="Choose photos"
          onDragEnter={() => setIsDragging(true)}
          onDragLeave={() => setIsDragging(false)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
        >
          <input type="file" accept="image/*" multiple onChange={handleInput} />
          <span className="dropzone-icon"><ImagePlus size={30} /></span>
        </label>

        {queue.length > 0 && (
          <section className="upload-manager" aria-label="Upload queue">
            <header className="queue-summary">
              <div><strong>{queue.length} {queue.length === 1 ? "file" : "files"}</strong><span>{formatBytes(totalBytes)} total</span></div>
              {completed > 0 && <button className="text-button" type="button" onClick={clearCompleted}>Clear completed</button>}
            </header>
            <div className="upload-list">
              {queue.map((item, index) => (
                <article className="upload-item" key={item.id}>
                  <img src={item.previewUrl} alt="" />
                  <div className="upload-file">
                    <strong>Photo {index + 1}</strong>
                    <span>{formatBytes(item.file.size)}</span>
                    <div className="progress-track" aria-label={`${item.progress}% uploaded`}><span style={{ width: `${item.progress}%` }} /></div>
                    <small className={`upload-state ${item.status}`}>{uploadLabel(item)}</small>
                  </div>
                  <div className="queue-actions">
                    {item.status === "uploading" && <button className="icon-button" type="button" onClick={() => cancelItem(item)} aria-label="Cancel upload" title="Cancel"><X size={17} /></button>}
                    {(item.status === "error" || item.status === "cancelled") && <button className="icon-button" type="button" onClick={() => retryItem(item)} disabled={isUploading} aria-label="Retry upload" title="Retry"><RefreshCw size={17} /></button>}
                    {item.status === "complete" ? <span className="complete-mark" title="Uploaded"><Check size={18} /></span> : (
                      item.status !== "uploading" && <button className="icon-button danger-button" type="button" onClick={() => removeItem(item)} aria-label="Remove from queue" title="Remove"><Trash2 size={17} /></button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <div className="actions upload-actions">
          <button type="button" disabled={isUploading || queued.length === 0} onClick={() => void runUploads(queued)}>
            {isUploading ? <Loader2 className="spin" size={18} /> : <Upload size={18} />}
            Upload
          </button>
          <a className="secondary link-button" href={`/g/${slug}`}>View gallery</a>
        </div>
        {message && <p className="status" role="status">{message}</p>}
      </section>
    </ThemedShell>
  );
}

function parseResponse(value: string) {
  try {
    return JSON.parse(value) as { error?: unknown; photo?: unknown };
  } catch {
    return {} as { error?: unknown; photo?: unknown };
  }
}

function uploadLabel(item: UploadItem) {
  if (item.status === "uploading") return `Uploading ${item.progress}%`;
  if (item.status === "complete") return "Original uploaded";
  if (item.status === "error" || item.status === "cancelled") return item.error;
  return "Ready";
}
